import { useState, useEffect } from 'react'
import { FileText, AlertCircle, Star } from 'lucide-react'
import { api } from '@/lib/api'

export interface Scan {
  id: number
  scan_type: string | null
  subject: string | null
  title: string | null
  grade_detected: number | null
  xp_awarded: number
  image_path: string | null
  is_friday_folder: boolean
  created_at: string
  needs_categorization: boolean
}

interface ScanCardProps {
  scan: Scan
  onClick: () => void
  studentId?: number
  isParentView?: boolean
}

const TYPE_COLORS: Record<string, string> = {
  test: 'bg-red-500/20 text-red-400',
  quiz: 'bg-orange-500/20 text-orange-400',
  homework: 'bg-blue-500/20 text-blue-400',
  worksheet: 'bg-green-500/20 text-green-400',
  classwork: 'bg-purple-500/20 text-purple-400',
  friday_folder: 'bg-yellow-500/20 text-yellow-400',
  other: 'bg-gray-500/20 text-gray-400',
}

const GRADE_COLORS = {
  excellent: 'text-green-400',
  good: 'text-blue-400',
  fair: 'text-yellow-400',
  poor: 'text-red-400',
}

function getGradeColor(grade: number | null): string {
  if (grade === null) return 'text-gray-400'
  if (grade >= 90) return GRADE_COLORS.excellent
  if (grade >= 80) return GRADE_COLORS.good
  if (grade >= 70) return GRADE_COLORS.fair
  return GRADE_COLORS.poor
}

export function ScanCard({ scan, onClick, studentId, isParentView = false }: ScanCardProps) {
  const displayTitle = scan.title || scan.subject || scan.scan_type || 'Untitled Scan'
  const typeColor = scan.scan_type ? TYPE_COLORS[scan.scan_type] || TYPE_COLORS.other : TYPE_COLORS.other
  const [thumbUrl, setThumbUrl] = useState<string | null>(null)

  // Fetch thumbnail via authenticated API (same pattern as ScanDetailModal)
  useEffect(() => {
    let currentUrl: string | null = null

    if (!scan.image_path) return

    const imageEndpoint = isParentView && studentId
      ? `/api/parent/students/${studentId}/scans/${scan.id}/image`
      : `/api/student/scans/${scan.id}/image`

    const fetchThumb = async () => {
      try {
        const response = await api.get(imageEndpoint, { responseType: 'blob' })
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' })
        currentUrl = URL.createObjectURL(blob)
        setThumbUrl(currentUrl)
      } catch {
        // Silently fail — fallback icon will show
      }
    }

    fetchThumb()

    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [scan.id, scan.image_path, studentId, isParentView])

  const scanDate = new Date(scan.created_at)
  const formattedDate = scanDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  const formattedTime = scanDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-surface-card/50 hover:bg-surface-hover border border-gray-700/50 hover:border-gray-600 rounded-lg p-4 transition-all duration-200 group"
    >
      <div className="flex items-start gap-3">
        {/* Icon/Thumbnail area */}
        <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt=""
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling?.classList.remove('hidden')
              }}
            />
          ) : null}
          <FileText className={`w-6 h-6 text-gray-500 ${thumbUrl ? 'hidden' : ''}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium text-white truncate group-hover:text-quest-primary transition-colors">
                {displayTitle}
              </h3>
              <p className="text-sm text-gray-400">{formattedDate} at {formattedTime}</p>
            </div>

            {/* Grade badge */}
            {scan.grade_detected !== null && (
              <div
                className={`px-2 py-0.5 rounded text-sm font-medium ${getGradeColor(
                  scan.grade_detected
                )}`}
              >
                {scan.grade_detected}%
              </div>
            )}
          </div>

          {/* Tags row */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {/* Type badge */}
            {scan.scan_type && (
              <span className={`px-2 py-0.5 rounded text-xs ${typeColor}`}>
                {scan.scan_type}
              </span>
            )}

            {/* Subject badge */}
            {scan.subject && (
              <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">
                {scan.subject}
              </span>
            )}

            {/* Friday folder indicator */}
            {scan.is_friday_folder && (
              <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                <Star className="w-3 h-3" />
                Friday
              </span>
            )}

            {/* XP earned */}
            {scan.xp_awarded > 0 && (
              <span className="text-xs text-quest-accent">+{scan.xp_awarded} XP</span>
            )}

            {/* Needs categorization warning */}
            {scan.needs_categorization && (
              <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Needs Info
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}
